import { observer } from 'mobx-react-lite';
import { when } from 'mobx';
import { message } from 'antd';
import EPButton from '@/components/EPButton/EPButton';
import { useStores } from '@/hooks/use-stores';
import { useTranslation } from 'react-i18next';
import { useStage } from '../../Provider';
import { LifecycleStageId } from '../../../constants';
import { useState } from 'react';
import { ButtonContent } from './MoveToStageButton.styles';

interface MoveToStageButtonProps {
  transition: {
    id: LifecycleStageId;
    name: string;
    skippedStages?: string[];
  };
}

export const MoveToStageButton = observer(({ transition }: MoveToStageButtonProps) => {
  const { lifecycleStore } = useStores();
  const { t } = useTranslation();
  const { isReadOnly } = useStage();
  const [isLoading, setIsLoading] = useState(false);

  const onMoveToStageClick = async () => {
    setIsLoading(true);

    // Wait for any ongoing task saves to complete (triggered by blur)
    if (lifecycleStore.isSavingTasks) {
      await when(() => !lifecycleStore.isSavingTasks);
    }

    const allTasksCompleted = lifecycleStore.validateStageTasks();

    if (!allTasksCompleted) {
      setIsLoading(false);
      return;
    }

    message.loading({ content: t('Moving stage...'), key: 'move-to-stage', duration: 10 });
    const isMoved = await lifecycleStore.moveStage(transition.id);
    if (isMoved) {
      message.success({
        content: `${t('Move stage success!')} ${t('new stage:')} ${t(transition.name)}`,
        key: 'move-to-stage',
      });
    } else {
      message.error({ content: t('Move stage failed'), key: 'move-to-stage' });
    }
    setIsLoading(false);
  };

  const hasSkippedStages = transition.skippedStages && transition.skippedStages.length > 0;

  return (
    <EPButton
      key={transition.id}
      type="primary"
      onClick={onMoveToStageClick}
      disabled={isReadOnly}
      loading={isLoading}
      style={{ padding: '2px 8px', height: 'auto' }}
    >
      <ButtonContent>
        <span>{`${t('Move to')} ${t(transition.name)}`}</span>
        {hasSkippedStages && (
          <span className="skip-text">
            ({t('Skip')} {transition.skippedStages.map(stage => t(stage)).join(', ')})
          </span>
        )}
      </ButtonContent>
    </EPButton>
  );
});
